// Library methods
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import CustomSwitch from "src/components/shared/inputs/CustomSwitch/CustomSwitch";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import ExamTypePicker from "../ExamTypePicker/ExamTypePicker";
import FormField from "../FormField/FormField";
import InfoNote from "../InfoNote/InfoNote";
import WorklistTiming from "../WorklistTiming/WorklistTiming";
import PollInterval from "../PollInterval/PollInterval";
import { ACCENT_TEAL } from "../ChoiceCard/ChoiceCard";

// Utils
import {
  DicomAddress,
  PRIOR_HISTORY_MONTHS_OPTIONS,
  PacServerFormValues,
  needsWorklist,
  remoteAeTitleLabel,
} from "src/utils/emr/pacServerHelper";
import { PRIOR_HISTORY_LABELS } from "src/utils/emr/jobDescriptionHelper";

// Types
import {
  EMRCatalogDeviceType,
  EMRRetrieveMethod,
} from "src/services/emr/types";

// Styles
import {
  ModalCustomConfirmLink,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

const DIGITS_ONLY = /[^0-9]/g;

type AddressFieldsType = {
  idPrefix: string;
  value: DicomAddress;
  disabled: boolean;
  remoteAeLabel: string;
  showHost: boolean;
  onChange: (value: DicomAddress) => void;
};

export const AddressFields = ({
  idPrefix,
  value,
  disabled,
  remoteAeLabel,
  showHost,
  onChange,
}: AddressFieldsType) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      {showHost && (
        <Box display="flex" gap="8px">
          <Box flex={2} minWidth={0} display="flex">
            <FormField
              label={t("Host / IP")}
              htmlFor={`${idPrefix}-host-input`}
              required
            >
              <TextInputField
                id={`${idPrefix}-host-input`}
                placeholder="10.0.0.20"
                value={value.host}
                disabled={disabled}
                maxLength={260}
                onChange={(event) =>
                  onChange({ ...value, host: event.target.value })
                }
              />
            </FormField>
          </Box>
          <FormField
            label={t("Port")}
            htmlFor={`${idPrefix}-port-input`}
            required
          >
            <TextInputField
              id={`${idPrefix}-port-input`}
              placeholder="104"
              value={value.port}
              disabled={disabled}
              maxLength={5}
              onChange={(event) =>
                onChange({
                  ...value,
                  port: event.target.value.replace(DIGITS_ONLY, ""),
                })
              }
            />
          </FormField>
        </Box>
      )}
      <Box display="flex" gap="8px">
        <FormField
          label={remoteAeLabel}
          htmlFor={`${idPrefix}-remote-ae-input`}
          required
        >
          <TextInputField
            id={`${idPrefix}-remote-ae-input`}
            placeholder="PACS"
            value={value.remoteAeTitle}
            disabled={disabled}
            maxLength={16}
            onChange={(event) =>
              onChange({ ...value, remoteAeTitle: event.target.value })
            }
          />
        </FormField>
        <FormField
          label={t("Hub AE title")}
          htmlFor={`${idPrefix}-local-ae-input`}
          required
        >
          <TextInputField
            id={`${idPrefix}-local-ae-input`}
            placeholder="EMRHUB"
            value={value.localAeTitle}
            disabled={disabled}
            maxLength={16}
            onChange={(event) =>
              onChange({ ...value, localAeTitle: event.target.value })
            }
          />
        </FormField>
      </Box>
    </Box>
  );
};

type JobPanelType = {
  idPrefix: string;
  title: string;
  description: ReactNode;
  enabled: boolean;
  badge?: string;
  onToggle: (enabled: boolean) => void;
  moreOptions: ReactNode;
  moreOptionsOpenByDefault: boolean;
  children?: ReactNode;
};

const JobPanel = ({
  idPrefix,
  title,
  description,
  enabled,
  badge,
  onToggle,
  moreOptions,
  moreOptionsOpenByDefault,
  children,
}: JobPanelType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, mutedText } = getSurfaceTokens(theme);
  const isDark = theme.palette.mode === "dark";
  const [showMore, setShowMore] = useState(moreOptionsOpenByDefault);

  return (
    <Box
      component="fieldset"
      data-testid={`${idPrefix}-panel`}
      sx={{
        margin: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        padding: enabled ? "13px" : "14px",
        borderRadius: "8px",
        border: enabled
          ? `2px solid ${ACCENT_TEAL}`
          : `1px solid ${isDark ? "#8A8DA2" : "rgba(0, 0, 0, 0.2)"}`,
        backgroundColor: enabled
          ? isDark
            ? "#2A2C3F"
            : "rgba(27, 143, 118, 0.06)"
          : "transparent",
      }}
    >
      <Box display="flex" alignItems="flex-start" gap="10px">
        <CustomSwitch
          checked={enabled}
          onChange={(event) => onToggle(event.target.checked)}
          inputProps={{ "aria-label": title, role: "switch" }}
          sx={{ marginTop: "-8px" }}
        />
        <Box flex={1} minWidth={0}>
          <Box display="flex" gap="8px" alignItems="center" flexWrap="wrap">
            <Typography
              component="legend"
              fontSize="15px"
              fontWeight={800}
              color={onSurface}
              sx={{ padding: 0 }}
            >
              {title}
            </Typography>
            {badge && (
              <Typography
                fontSize="11px"
                fontWeight={800}
                sx={{
                  color: "#3B1F0B",
                  backgroundColor: "#FFC48A",
                  borderRadius: "999px",
                  padding: "2px 8px",
                }}
              >
                {badge}
              </Typography>
            )}
          </Box>
          <Typography fontSize="12px" color={mutedText} lineHeight={1.4}>
            {description}
          </Typography>
        </Box>
      </Box>

      {enabled && (
        <Box display="flex" flexDirection="column" gap="12px" pl="58px">
          {children}
          <Box
            sx={{
              borderTop: `1px solid ${isDark ? "#4F5266" : "rgba(0,0,0,0.12)"}`,
              paddingTop: "8px",
            }}
          >
            <Button
              sx={ModalCustomConfirmLink}
              aria-expanded={showMore}
              aria-controls={`${idPrefix}-more-options`}
              onClick={() => setShowMore((previous) => !previous)}
            >
              {t("More options")}
            </Button>
            {showMore && (
              <Box
                id={`${idPrefix}-more-options`}
                display="flex"
                flexDirection="column"
                gap="10px"
                mt="8px"
              >
                {moreOptions}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const UseServerAddress = ({
  idPrefix,
  checked,
  serverAddress,
  onChange,
}: {
  idPrefix: string;
  checked: boolean;
  serverAddress: DicomAddress;
  onChange: (checked: boolean) => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  return (
    <Box
      component="label"
      htmlFor={`${idPrefix}-use-server-address`}
      display="flex"
      gap="8px"
      alignItems="center"
      sx={{ cursor: "pointer" }}
    >
      <Checkbox
        id={`${idPrefix}-use-server-address`}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        sx={{
          padding: "2px",
          color: onSurface,
          "&.Mui-checked": { color: ACCENT_TEAL },
        }}
      />
      <Typography fontSize="13px" color={onSurface}>
        {t("Use the PAC server's address ({{address}})", {
          address: `${serverAddress.host || "-"} : ${serverAddress.port}, ${serverAddress.remoteAeTitle || "-"}`,
        })}
      </Typography>
    </Box>
  );
};

export type PacServerJobKey = "worklist" | "exams" | "reports";

const ALL_JOBS: Array<PacServerJobKey> = ["worklist", "exams", "reports"];

type CutoverDateFieldType = {
  idPrefix: string;
  value: string;
  onChange: (value: string) => void;
};

export const CutoverDateField = ({
  idPrefix,
  value,
  onChange,
}: CutoverDateFieldType) => {
  const { t } = useTranslation();

  return (
    <Box maxWidth="220px">
      <FormField
        label={t("Send reports of exams from")}
        htmlFor={`${idPrefix}-export-from-input`}
        required
      >
        <TextInputField
          id={`${idPrefix}-export-from-input`}
          type="date"
          placeholder=""
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </FormField>
    </Box>
  );
};

type PacServerJobsFormType = {
  idPrefix: string;
  values: PacServerFormValues;
  device: EMRCatalogDeviceType | undefined;
  hubHasOtherWorklist: boolean;
  onChange: (values: PacServerFormValues) => void;
  jobs?: Array<PacServerJobKey>;
};

const PacServerJobsForm = ({
  idPrefix,
  values,
  device,
  hubHasOtherWorklist,
  onChange,
  jobs = ALL_JOBS,
}: PacServerJobsFormType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, mutedText } = getSurfaceTokens(theme);
  const serverName = values.name || t("the PAC server");
  const { worklist, exams, reports } = values;

  const historyOptions = Array.from(
    new Set([...PRIOR_HISTORY_MONTHS_OPTIONS, values.exams.priorHistoryMonths])
  )
    .sort((left, right) => left - right)
    .map((months) => ({
      displayName: PRIOR_HISTORY_LABELS[months]
        ? t(PRIOR_HISTORY_LABELS[months])
        : t("the last {{months}} months", { months }),
      value: String(months),
    }));

  const retrieveMethodOptions: Array<{
    displayName: string;
    value: EMRRetrieveMethod;
  }> = [
    { displayName: t("on the same connection (C-GET)"), value: "CGet" },
    { displayName: t("back to the hub (C-MOVE)"), value: "CMove" },
  ];

  const update = <K extends "worklist" | "exams" | "reports">(
    key: K,
    changes: Partial<PacServerFormValues[K]>
  ) => onChange({ ...values, [key]: { ...values[key], ...changes } });

  const remoteAeLabel = remoteAeTitleLabel(device, t);

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      {jobs.includes("worklist") && (
        <JobPanel
          idPrefix={`${idPrefix}-worklist`}
          title={t("Read the worklist")}
          description={t("Who is booked, so exams land on the right patient.")}
          enabled={worklist.enabled}
          badge={!worklist.useServerAddress ? t("Own address") : undefined}
          onToggle={(enabled) => update("worklist", { enabled })}
          moreOptionsOpenByDefault={!worklist.useServerAddress}
          moreOptions={
            <>
              <UseServerAddress
                idPrefix={`${idPrefix}-worklist`}
                checked={worklist.useServerAddress}
                serverAddress={values.address}
                onChange={(checked) =>
                  update("worklist", {
                    useServerAddress: checked,
                    ...(!checked && { address: values.address }),
                  })
                }
              />
              <AddressFields
                idPrefix={`${idPrefix}-worklist`}
                value={
                  worklist.useServerAddress ? values.address : worklist.address
                }
                disabled={worklist.useServerAddress}
                remoteAeLabel={remoteAeLabel}
                showHost
                onChange={(address) => update("worklist", { address })}
              />
              <Box maxWidth="260px">
                <FormField
                  label={t("Scheduled station AE title")}
                  htmlFor={`${idPrefix}-worklist-station-ae-input`}
                >
                  <TextInputField
                    id={`${idPrefix}-worklist-station-ae-input`}
                    placeholder={t("Optional")}
                    value={worklist.scheduledStationAeTitle}
                    maxLength={16}
                    onChange={(event) =>
                      update("worklist", {
                        scheduledStationAeTitle: event.target.value,
                      })
                    }
                  />
                </FormField>
              </Box>
            </>
          }
        >
          <WorklistTiming
            idPrefix={`${idPrefix}-worklist`}
            value={worklist.timing}
            onChange={(timing) => update("worklist", { timing })}
          />
        </JobPanel>
      )}

      {jobs.includes("exams") && (
        <JobPanel
          idPrefix={`${idPrefix}-exams`}
          title={t("Bring in their exams")}
          description={
            exams.delivery === "Push"
              ? t(
                  "{{name}} pushes exams to the hub. The worklist filter does not apply.",
                  { name: serverName }
                )
              : t(
                  "Only for patients on the worklist. Nobody else's exams leave {{name}}.",
                  { name: serverName }
                )
          }
          enabled={exams.enabled}
          badge={!exams.useServerAddress ? t("Own address") : undefined}
          onToggle={(enabled) => update("exams", { enabled })}
          moreOptionsOpenByDefault={
            !exams.useServerAddress || exams.delivery === "Push"
          }
          moreOptions={
            <>
              <Box
                component="label"
                htmlFor={`${idPrefix}-exams-push`}
                display="flex"
                gap="8px"
                alignItems="center"
                sx={{ cursor: "pointer" }}
              >
                <Checkbox
                  id={`${idPrefix}-exams-push`}
                  checked={exams.delivery === "Push"}
                  onChange={(event) =>
                    update("exams", {
                      delivery: event.target.checked ? "Push" : "Pull",
                    })
                  }
                  sx={{
                    padding: "2px",
                    color: onSurface,
                    "&.Mui-checked": { color: ACCENT_TEAL },
                  }}
                />
                <Typography fontSize="13px" color={onSurface}>
                  {t("Let {{name}} push exams instead", { name: serverName })}
                </Typography>
              </Box>
              {exams.delivery === "Pull" && (
                <Box display="flex" flexDirection="column" gap="4px">
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="8px"
                    flexWrap="wrap"
                  >
                    <Typography
                      id={`${idPrefix}-exams-retrieve-method-label`}
                      fontSize="13px"
                      color={onSurface}
                    >
                      {t("{{name}} sends the images", { name: serverName })}
                    </Typography>
                    <Box minWidth="260px">
                      <SelectDropdown
                        id={`${idPrefix}-exams-retrieve-method-dropdown`}
                        labelId={`${idPrefix}-exams-retrieve-method-label`}
                        dropdownOptions={retrieveMethodOptions}
                        value={
                          retrieveMethodOptions.find(
                            (option) => option.value === exams.retrieveMethod
                          )?.displayName ?? ""
                        }
                        handleChange={(event) => {
                          const retrieveMethod = retrieveMethodOptions.find(
                            (option) =>
                              option.displayName === event.target.value
                          )?.value;
                          if (retrieveMethod) {
                            update("exams", { retrieveMethod });
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography
                    fontSize="12px"
                    color={mutedText}
                    lineHeight={1.4}
                  >
                    {exams.retrieveMethod === "CGet"
                      ? t("Nothing to set up on {{name}}.", {
                          name: serverName,
                        })
                      : t(
                          "{{name}} must have the hub registered as a destination, and the hub must accept incoming DICOM.",
                          { name: serverName }
                        )}
                  </Typography>
                  {exams.retrieveMethod === "CMove" && (
                    <Box maxWidth="260px" mt="8px">
                      <FormField
                        label={t("Listener port")}
                        htmlFor={`${idPrefix}-exams-move-listener-port-input`}
                        required
                      >
                        <TextInputField
                          id={`${idPrefix}-exams-move-listener-port-input`}
                          placeholder="11112"
                          value={exams.moveListenerPort}
                          maxLength={5}
                          helperText={t(
                            "The port {{name}} sends studies to on the hub PC. Register the Hub AE title with this port on the PAC.",
                            { name: serverName }
                          )}
                          onChange={(event) =>
                            update("exams", {
                              moveListenerPort: event.target.value.replace(
                                DIGITS_ONLY,
                                ""
                              ),
                            })
                          }
                        />
                      </FormField>
                    </Box>
                  )}
                </Box>
              )}
              {exams.delivery === "Push" && (
                <Box maxWidth="200px">
                  <FormField
                    label={t("Hub port")}
                    htmlFor={`${idPrefix}-exams-listen-port-input`}
                    required
                  >
                    <TextInputField
                      id={`${idPrefix}-exams-listen-port-input`}
                      placeholder="104"
                      value={exams.listenPort}
                      maxLength={5}
                      onChange={(event) =>
                        update("exams", {
                          listenPort: event.target.value.replace(
                            DIGITS_ONLY,
                            ""
                          ),
                        })
                      }
                    />
                  </FormField>
                </Box>
              )}
              <UseServerAddress
                idPrefix={`${idPrefix}-exams`}
                checked={exams.useServerAddress}
                serverAddress={values.address}
                onChange={(checked) =>
                  update("exams", {
                    useServerAddress: checked,
                    ...(!checked && { address: values.address }),
                  })
                }
              />
              <AddressFields
                idPrefix={`${idPrefix}-exams`}
                value={exams.useServerAddress ? values.address : exams.address}
                disabled={exams.useServerAddress}
                remoteAeLabel={remoteAeLabel}
                showHost={exams.delivery === "Pull"}
                onChange={(address) => update("exams", { address })}
              />
            </>
          }
        >
          <ExamTypePicker
            options={device?.examTypes ?? exams.examTypes}
            value={exams.examTypes}
            onChange={(examTypes) => update("exams", { examTypes })}
          />
          {exams.examTypes.length === 0 && (
            <Typography fontSize="12px" color={mutedText}>
              {t("Pick at least one exam type.")}
            </Typography>
          )}
          {exams.delivery === "Pull" && (
            <Box display="flex" alignItems="center" gap="8px" flexWrap="wrap">
              <Typography
                id={`${idPrefix}-exams-history-label`}
                fontSize="13px"
                color={onSurface}
              >
                {t("The first time a patient is booked, also bring")}
              </Typography>
              <Box minWidth="180px">
                <SelectDropdown
                  id={`${idPrefix}-exams-history-dropdown`}
                  labelId={`${idPrefix}-exams-history-label`}
                  dropdownOptions={historyOptions}
                  value={
                    historyOptions.find(
                      (option) =>
                        option.value === String(exams.priorHistoryMonths)
                    )?.displayName ?? ""
                  }
                  handleChange={(event) => {
                    const months = historyOptions.find(
                      (option) => option.displayName === event.target.value
                    )?.value;
                    if (months !== undefined) {
                      update("exams", { priorHistoryMonths: Number(months) });
                    }
                  }}
                />
              </Box>
            </Box>
          )}
          {exams.delivery === "Pull" && (
            <PollInterval
              idPrefix={`${idPrefix}-exams`}
              minutes={exams.pollIntervalMinutes}
              onChange={(pollIntervalMinutes) =>
                update("exams", { pollIntervalMinutes })
              }
            />
          )}
          {needsWorklist(values, hubHasOtherWorklist) && (
            <InfoNote testId={`${idPrefix}-worklist-required`} tone="warning">
              {t(
                "Exams are brought in only for patients on a worklist. Switch on the worklist, or add a worklist from a shared folder first."
              )}
            </InfoNote>
          )}
        </JobPanel>
      )}

      {jobs.includes("reports") && (
        <JobPanel
          idPrefix={`${idPrefix}-reports`}
          title={t("Send RetinaLogik reports back")}
          description={t("Stored in {{name}} next to the original exam.", {
            name: serverName,
          })}
          enabled={reports.enabled}
          badge={!reports.useServerAddress ? t("Own address") : undefined}
          onToggle={(enabled) => update("reports", { enabled })}
          moreOptionsOpenByDefault={!reports.useServerAddress}
          moreOptions={
            <>
              <UseServerAddress
                idPrefix={`${idPrefix}-reports`}
                checked={reports.useServerAddress}
                serverAddress={values.address}
                onChange={(checked) =>
                  update("reports", {
                    useServerAddress: checked,
                    ...(!checked && { address: values.address }),
                  })
                }
              />
              <AddressFields
                idPrefix={`${idPrefix}-reports`}
                value={
                  reports.useServerAddress ? values.address : reports.address
                }
                disabled={reports.useServerAddress}
                remoteAeLabel={remoteAeLabel}
                showHost
                onChange={(address) => update("reports", { address })}
              />
              <Box maxWidth="200px">
                <FormField
                  label={t("Timeout (seconds)")}
                  htmlFor={`${idPrefix}-reports-timeout-input`}
                >
                  <TextInputField
                    id={`${idPrefix}-reports-timeout-input`}
                    placeholder="30"
                    value={reports.associationTimeoutSeconds}
                    maxLength={3}
                    helperText={t("Between 5 and 300.")}
                    onChange={(event) =>
                      update("reports", {
                        associationTimeoutSeconds: event.target.value.replace(
                          DIGITS_ONLY,
                          ""
                        ),
                      })
                    }
                  />
                </FormField>
              </Box>
            </>
          }
        >
          <CutoverDateField
            idPrefix={`${idPrefix}-reports`}
            value={reports.exportFrom}
            onChange={(exportFrom) => update("reports", { exportFrom })}
          />
        </JobPanel>
      )}
    </Box>
  );
};

export default PacServerJobsForm;
